import { Typography } from '@mui/material';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { uniqBy } from 'lodash';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';
import DistributableActionsHistory from './DistributableActionsHistory';

type Props = {
  index: number;
  baseCurrency: CurrencyCode;
  onAmountChange: (
    commitmentId: string,
    amount: number,
    field: 'outsideOfCarry' | 'recallableOutsideOfCarry',
  ) => void;
  fundsRemainingToDistribute: Array<{
    commitmentId: string;
    investorLabel: string;
    outsideOfCarryCalculation: {
      fundsFromIncomes: number;
      fundsFromFxGains: number;
      distributedToDate: number;
      actionHistory: Array<{
        actionRef: string;
        actionId: string;
        amountLcy?: number | null;
        date: CalendarDay;
      }>;
    };
    availableToDistributeOutsideOfCarry: number;
  }>;
};

const OutsideOfCarryDistribution: React.FC<Props> = ({
  fundsRemainingToDistribute,
  baseCurrency,
  index,
  onAmountChange,
}) => {
  const {
    formMethods: { watch, formState },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const distributionValues = Object.fromEntries(
    (watch(`entries.${index}.capitalBreakdown`) ?? []).map((row) => [
      row.commitmentId,
      {
        total: row.fundsDistributed?.outsideOfCarry ?? 0,
        recallable: row.fundsDistributed?.recallableOutsideOfCarry ?? 0,
      },
    ]),
  );

  const actionHistory = uniqBy(
    fundsRemainingToDistribute.flatMap(
      (details) => details.outsideOfCarryCalculation.actionHistory,
    ),
    'actionId',
  );

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
    },
    {
      key: 'fundsFromIncomes',
      label: 'Income',
    },
    {
      key: 'fundsFromFxGains',
      label: 'Realised FX gains on incomes/expenses',
    },
    {
      key: 'distributedToDate',
      label: 'Total distributions',
    },
    {
      key: 'undistributed',
      label: 'Undistributed',
    },
    {
      key: 'distribution',
      label: 'Distribution',
      render: (row: { commitmentId: string }) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.total ?? 0}
          onChange={(value) => {
            onAmountChange(row.commitmentId, value ?? 0, 'outsideOfCarry');
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          clearZeroValueOnFocus
        />
      ),
    },
    {
      key: 'recallableDistribution',
      label: 'Of which recallable',
      render: (row: { commitmentId: string }, rowIndex: number) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.recallable ?? 0}
          onChange={(value) => {
            onAmountChange(
              row.commitmentId,
              value ?? 0,
              'recallableOutsideOfCarry',
            );
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          clearZeroValueOnFocus
          error={
            !!formState.errors.entries?.[index]?.capitalBreakdown?.[rowIndex]
              ?.fundsDistributed?.recallableOutsideOfCarry
          }
        />
      ),
    },
  ];

  const rows = fundsRemainingToDistribute.map((details) => ({
    id: details.commitmentId,
    commitmentId: details.commitmentId,
    investorName: details.investorLabel,
    fundsFromFxGains: numberToCurrencyString(
      details.outsideOfCarryCalculation.fundsFromFxGains,
      {
        currency: baseCurrency,
      },
    ),
    fundsFromIncomes: numberToCurrencyString(
      details.outsideOfCarryCalculation.fundsFromIncomes,
      {
        currency: baseCurrency,
      },
    ),
    distributedToDate: numberToCurrencyString(
      details.outsideOfCarryCalculation.distributedToDate,
      {
        currency: baseCurrency,
      },
    ),
    undistributed: numberToCurrencyString(
      details.availableToDistributeOutsideOfCarry,
      {
        currency: baseCurrency,
      },
    ),
  }));

  return (
    <Column spacing="sm">
      <Typography variant="h4">
        Process distribution (not subject to carried interest)
      </Typography>
      <DistributableActionsHistory
        baseCurrency={baseCurrency}
        actionHistory={actionHistory}
      />
      <Table columns={columns} rows={rows} />
    </Column>
  );
};

export default OutsideOfCarryDistribution;
